import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

function About() {
	return (
		<>
			<Helmet>
				<title>About Us | Apex Specialty Contracting LLC – Tarpon Springs, FL</title>
				<meta name="description" content="Learn about Apex Specialty Contracting LLC, a licensed and insured roofing and storm restoration company based in Tarpon Springs, FL. Serving the Tampa Bay area since 2021." />
			</Helmet>
			<PageHeader
				title="About Apex Specialty Contracting"
				subtitle="A trusted local roofing company built on quality craftsmanship, honest pricing, and reliable communication."
			/>

			{/* Company Story */}
			<section className="section">
				<div className="container two-col">
					<div>
						<h2>Our Story</h2>
						<p className="helper">
							Apex Specialty Contracting LLC was founded in 2021 in Tarpon Springs, Florida, with a simple mission: to provide homeowners and businesses in the Tampa Bay area with high-quality roofing and storm restoration services they can actually count on.
						</p>
						<p className="helper">
							Our founder grew up in the trades and watched too many homeowners get taken advantage of by contractors who showed up late, cut corners, or disappeared after the job. We started Apex to change that — to be the company that treats every customer's home the same way we'd treat our own family's home.
						</p>
						<p className="helper">
							Since opening our doors, we have completed more than 500 roofing projects across Pinellas, Pasco, and Hillsborough counties. Every project we take on is backed by our 5-year workmanship warranty and our personal commitment to getting the job done right the first time.
						</p>
						<div className="badges" style={{ marginTop: 16 }}>
							<span className="badge">Founded 2021</span>
							<span className="badge">FL Licensed Contractor</span>
							<span className="badge">Fully Insured</span>
							<span className="badge">500+ Projects Completed</span>
						</div>
					</div>
					<div className="img-frame fade-up">
						<img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600&auto=format&fit=crop" alt="Apex roofing crew on the job" />
					</div>
				</div>
			</section>

			{/* Mission & Values */}
			<section className="section section-alt">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 28 }}>
						<h2>Our Mission &amp; Values</h2>
						<p className="helper" style={{ maxWidth: 540, margin: '0 auto' }}>Everything we do comes back to three core principles: quality, honesty, and communication.</p>
					</div>
					<div className="grid grid-3">
						<div className="card" style={{ padding: 22 }}>
							<div className="icon-circle">
								<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3>Quality First</h3>
							<p className="helper">We only use manufacturer-approved materials and follow industry best practices on every project, regardless of size. Every roof we install is built to last.</p>
						</div>
						<div className="card" style={{ padding: 22 }}>
							<div className="icon-circle">
								<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20z" stroke="currentColor" strokeWidth="1.5"/></svg>
							</div>
							<h3>Honest Pricing</h3>
							<p className="helper">No bait-and-switch, no hidden fees. We provide detailed written estimates before any work begins and never change the price unless the scope changes — and always with your approval.</p>
						</div>
						<div className="card" style={{ padding: 22 }}>
							<div className="icon-circle">
								<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3>Clear Communication</h3>
							<p className="helper">You'll always know where your project stands. We send opt-in SMS updates at key project milestones and our project managers are available by phone, email, or text.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Team */}
			<section className="section">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 28 }}>
						<h2>Meet Our Team</h2>
						<p className="helper" style={{ maxWidth: 540, margin: '0 auto' }}>Our experienced team of roofing professionals is committed to delivering exceptional results on every project.</p>
					</div>
					<div className="grid grid-3">
						<div className="card" style={{ padding: 24, textAlign: 'center' }}>
							<div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eef6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
								<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3 style={{ margin: '0 0 4px' }}>Marcus Webb</h3>
							<p className="helper" style={{ margin: '0 0 8px', fontWeight: 600 }}>Owner &amp; Founder</p>
							<p className="helper" style={{ margin: 0 }}>15+ years in commercial and residential roofing. Licensed Florida Roofing Contractor. Personally oversees every project Apex takes on.</p>
						</div>
						<div className="card" style={{ padding: 24, textAlign: 'center' }}>
							<div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eef6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
								<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3 style={{ margin: '0 0 4px' }}>Diana Reyes</h3>
							<p className="helper" style={{ margin: '0 0 8px', fontWeight: 600 }}>Operations &amp; Project Manager</p>
							<p className="helper" style={{ margin: 0 }}>Coordinates all active projects, handles scheduling, and is your main point of contact for updates and questions throughout your project.</p>
						</div>
						<div className="card" style={{ padding: 24, textAlign: 'center' }}>
							<div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eef6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
								<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3 style={{ margin: '0 0 4px' }}>James Kowalski</h3>
							<p className="helper" style={{ margin: '0 0 8px', fontWeight: 600 }}>Lead Estimator &amp; Inspector</p>
							<p className="helper" style={{ margin: 0 }}>Certified roof inspector with 10 years of experience. Handles all on-site assessments, estimates, and insurance documentation preparation.</p>
						</div>
						<div className="card" style={{ padding: 24, textAlign: 'center' }}>
							<div style={{ width: 80, height: 80, borderRadius: '50%', background: '#eef6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
								<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<h3 style={{ margin: '0 0 4px' }}>Biswanath Sahoo</h3>
							<p className="helper" style={{ margin: '0 0 8px', fontWeight: 600 }}>IT Consultant &amp; Technical Advisor</p>
							<p className="helper" style={{ margin: 0 }}>Oversees our digital infrastructure, customer communication systems, and technology integrations that keep projects running smoothly from estimate to completion.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Certifications & Licenses */}
			<section className="section section-alt">
				<div className="container">
					<h2>Licenses, Certifications &amp; Insurance</h2>
					<p className="helper">We hold all required state and local licenses and maintain comprehensive insurance to protect our clients and crew on every job.</p>
					<div className="grid" style={{ marginTop: 20 }}>
						<div className="card" style={{ padding: 18 }}>
							<div className="icon-circle">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<strong>Florida Roofing Contractor License</strong>
							<p className="helper">Licensed by the Florida Department of Business &amp; Professional Regulation (DBPR). License #XXXXX2536</p>
						</div>
						<div className="card" style={{ padding: 18 }}>
							<div className="icon-circle">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7l-9-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
							</div>
							<strong>General Liability Insurance</strong>
							<p className="helper">We carry $2,000,000 in general liability coverage on every project. Certificate of insurance available upon request.</p>
						</div>
						<div className="card" style={{ padding: 18 }}>
							<div className="icon-circle">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
							</div>
							<strong>Workers' Compensation</strong>
							<p className="helper">All employees and subcontractors are covered by workers' compensation insurance. You are never liable for on-site injuries on our projects.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Contact Info */}
			<section className="section">
				<div className="container">
					<h2>Get in Touch</h2>
					<div className="grid" style={{ marginTop: 14 }}>
						<div className="card" style={{ padding: 22 }}>
							<strong>Office Address</strong>
							<p className="helper">183 Highland Rd, Tarpon Springs, FL 34689</p>
							<strong>Phone</strong>
							<p className="helper"><a href="tel:9163160696">(916) 316-0696</a></p>
							<strong>Office Hours</strong>
							<p className="helper">Monday – Friday: 7:00 AM – 6:00 PM<br />Saturday: 8:00 AM – 2:00 PM (emergency calls taken 24/7)</p>
						</div>
						<div className="card" style={{ padding: 22 }}>
							<strong>Email</strong>
							<p className="helper">General Inquiries: <a href="mailto:info@appexconnecthub.com">info@appexconnecthub.com</a></p>
							<p className="helper">Customer Support: <a href="mailto:support@appexconnecthub.com">support@appexconnecthub.com</a></p>
							<strong>Social Media</strong>
							<p className="helper">Follow us for project photos, storm alerts, and roofing tips.</p>
							<div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
								<a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" className="social-icon-lg" aria-label="Facebook">
									<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
								</a>
								<a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="social-icon-lg" aria-label="Instagram">
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
								</a>
								<a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" className="social-icon-lg" aria-label="LinkedIn">
									<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
								</a>
							</div>
						</div>
					</div>
					<div style={{ marginTop: 20 }}>
						<Link className="btn" to="/contact">Schedule a Free Inspection</Link>
					</div>
				</div>
			</section>
		</>
	);
}

export default About;
